import { useEffect, useRef, useState } from "react";
import type { AgentThreadListItem } from "../../../../shared/types.ts";
import { displayThreadPreview, displayThreadTitle } from "../../lib/thread-title";

interface AgentUIHistoryPanelProps {
  activeThreadId: string | null;
  archivingThreadId?: string | null;
  brandName?: string | null;
  isLoading?: boolean;
  onArchive: (threadId: string) => void | Promise<void>;
  onNewThread: () => void;
  onSelect: (threadId: string) => void;
  threads: AgentThreadListItem[];
}

function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function AgentUIHistoryPanel({
  activeThreadId,
  archivingThreadId,
  brandName,
  isLoading,
  onArchive,
  onNewThread,
  onSelect,
  threads,
}: AgentUIHistoryPanelProps) {
  const [menuThreadId, setMenuThreadId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const archiveBusy = archivingThreadId !== null && archivingThreadId !== undefined;
  const label = brandName?.trim() || "Brand chat";

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target as Node)) {
        setMenuThreadId(null);
      }
    }

    window.addEventListener("mousedown", handlePointerDown);
    return () => window.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (menuThreadId && !threads.some((thread) => thread.id === menuThreadId)) {
      setMenuThreadId(null);
    }
  }, [menuThreadId, threads]);

  return (
    <aside className="flex h-full min-h-0 w-[244px] shrink-0 flex-col border-r border-slate-200 bg-slate-50/70">
      <div className="shrink-0 border-b border-slate-200 px-4 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Chats</p>
        <h2 className="mt-1 truncate text-xl font-semibold text-slate-900">{label}</h2>
      </div>
      <div className="shrink-0 px-3 py-3">
        <button
          type="button"
          data-testid="agent-history-new-thread"
          disabled={archiveBusy}
          onClick={onNewThread}
          className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span>New chat</span>
          <span className="font-mono text-xs text-slate-400">+</span>
        </button>
      </div>
      <div className="shrink-0 px-4 pb-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Recent</p>
      </div>
      <div
        data-testid="agent-history-scroll-viewport"
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-4 [scrollbar-gutter:stable]"
      >
        {isLoading ? <p className="px-2 py-6 text-xs text-slate-400">Loading chats…</p> : null}
        {!isLoading && threads.length === 0 ? (
          <p className="px-2 py-6 text-xs text-slate-400">No chats yet</p>
        ) : null}
        <div className="space-y-1">
          {threads.map((thread) => {
            const isActive = thread.id === activeThreadId;
            const preview = displayThreadPreview(thread);
            const menuOpen = menuThreadId === thread.id;
            const isArchiving = archivingThreadId === thread.id;
            return (
              <div
                key={thread.id}
                className={`group relative rounded-2xl transition ${
                  isActive ? "bg-white shadow-sm ring-1 ring-slate-200" : "hover:bg-white/70"
                }`}
              >
                <button
                  type="button"
                  data-testid={`thread-select-${thread.id}`}
                  disabled={archiveBusy}
                  onClick={() => onSelect(thread.id)}
                  className="block w-full min-w-0 rounded-2xl px-3 py-2 pr-11 text-left disabled:cursor-not-allowed"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-slate-800">{displayThreadTitle(thread)}</span>
                    <span className="shrink-0 font-mono text-[10px] text-slate-400">{timeAgo(thread.updated_at)}</span>
                  </div>
                  {preview ? <p className="mt-1 truncate text-xs text-slate-500">{preview}</p> : null}
                </button>

                <div
                  ref={menuOpen ? menuRef : null}
                  className="absolute right-2 top-2"
                >
                  <button
                    type="button"
                    data-testid={`thread-actions-${thread.id}`}
                    aria-label={`More options for ${displayThreadTitle(thread)}`}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    disabled={archiveBusy}
                    onClick={(event) => {
                      event.stopPropagation();
                      setMenuThreadId((current) => (current === thread.id ? null : thread.id));
                    }}
                    className={`rounded-full border border-transparent p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-40 ${
                      menuOpen
                        ? "bg-slate-100 text-slate-600"
                        : "opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100"
                    }`}
                  >
                    <span aria-hidden="true" className="block font-mono text-sm leading-none">•••</span>
                  </button>

                  {menuOpen ? (
                    <div className="absolute right-0 z-10 mt-2 w-40 rounded-2xl border border-slate-200 bg-white p-1 shadow-lg">
                      <button
                        type="button"
                        data-testid={`thread-archive-${thread.id}`}
                        disabled={archiveBusy}
                        onClick={async (event) => {
                          event.stopPropagation();
                          await onArchive(thread.id);
                          setMenuThreadId(null);
                        }}
                        className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <span>{isArchiving ? "Hiding…" : "Hide chat"}</span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">Archive</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
